import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { initialState } from '../../../data/setup/initialState.warranty.data';
import {
  autocompleteSearch as autocompleteSearchAPI,
  searchWarranty as searchWarrantyAPI,
  fetchWarrantyDetail as fetchWarrantyDetailAPI,
  fetchEntitlement as fetchEntitlementAPI,
  fetchSerialNoHistory as fetchSerialNoHistoryAPI,
} from '../../../apis/warrantyAPI';
import { ACTIONS_WARRANTY } from '../../../constants/actionCreator';
import { API_RESPONSE_STATUS } from '../../../constants/index';

// post api method to search autocomplete
export const autocompleteSearch = createAsyncThunk(
  ACTIONS_WARRANTY.AUTOCOMPLETE_SEARCH,
  async (body, { rejectWithValue }) => {
    try {
      const response = await autocompleteSearchAPI(body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// post api method to search given no
export const searchWarranty = createAsyncThunk(
  ACTIONS_WARRANTY.WARRANTY_SEARCH,
  async (body, { rejectWithValue }) => {
    try {
      const response = await searchWarrantyAPI(body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

//get api method to get detail
export const fetchDetail = createAsyncThunk(
  ACTIONS_WARRANTY.WARRANTY_DETAIL,
  async (body, { rejectWithValue }) => {
    try {
      const response = await fetchWarrantyDetailAPI(body);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

//entitlement
export const fetchEntitlement = createAsyncThunk(
  ACTIONS_WARRANTY.WARRANTY_ENTITLEMENT,
  async (body, { rejectWithValue }) => {
    try {
      const response = await fetchEntitlementAPI(body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

//sr no history
export const fetchHistory = createAsyncThunk(
  ACTIONS_WARRANTY.WARRANTY_SERIALNOHISTORY,
  async (body, { rejectWithValue }) => {
    try {
      const response = await fetchSerialNoHistoryAPI(body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const warrantySlice = createSlice({
  name: 'warranty',
  initialState,
  reducers: {
    handleAlertMessage: (state, action) => {
      state.alertMessage = action?.payload;
    },
    setIsSearchPage: (state, action) => {
      state.isSearchPage = action?.payload;
    },
    setIsDisplayButton: (state, action) => {
      state.isDisplayButton = action?.payload;
    },
    wnErrorSet: (state, action) => {
      state.wnError = action?.payload;
    },
    snErrorSet: (state, action) => {
      state.snError = action?.payload;
    },
    setRadioSelectedOption: (state, action) => {
      state.radioSelectedOption = action?.payload;
      state.searchTerm = initialState.searchTerm;
      state.selectedValWN.warrantyPartNumber =
        initialState.selectedValWN.warrantyPartNumber;
    },
    setAutocompleteOption: (state, action) => {
      state.autocompleteResult = [];
      state.filterOptions = [];
      const {
        warrantyPartNumber,
        isSearchByWarrantyPartNumber,
        serialNumber,
        pageIndex,
        recordCount,
      } = action?.payload;
      state.autocompleteOption = {
        ...state.autocompleteOption,
        warrantyPartNumber,
        isSearchByWarrantyPartNumber,
        serialNumber,
        pageIndex,
        recordCount,
      };
    },
    setCurrentSerialNumber: (state, action) => {
      const {
        warrantyPartNumber,
        isSearchByWarrantyPartNumber,
        serialNumber,
        pageIndex,
        recordCount,
      } = action?.payload;

      state.selectedValSN = {
        ...state.selectedValWN,
        warrantyPartNumber,
        isSearchByWarrantyPartNumber,
        serialNumber,
        pageIndex,
        recordCount,
      };
    },
    setWarrantyPartNumber: (state, action) => {
      const {
        warrantyPartNumber,
        isSearchByWarrantyPartNumber,
        serialNumber,
        pageIndex,
        recordCount,
      } = action?.payload;

      state.selectedValWN = {
        ...state.selectedValWN,
        warrantyPartNumber,
        isSearchByWarrantyPartNumber,
        serialNumber,
        pageIndex,
        recordCount,
      };
    },
    handleSearchTerm: (state, action) => {
      state.searchTerm = action?.payload;
    },
    handleSearchTermObj: (state, action) => {
      state.loading = action?.payload ? true : false;
      state.selectedSearchTerm = action?.payload;
      state.searchTerm = state.selectedSearchTerm;
    },
    handleReset: () => initialState,
    setWarrantyId: (state, action) => {
      state.warrantyId = action?.payload;
    },
    setFilterOptions: (state, action) => {
      state.filterOptions = action?.payload;
    },
    setDetailWarrantyNumber: (state, action) => {
      state.detailWarrantyPartNumber = action?.payload;
    },
    alertOpen: (state) => {
      state.isAlert = true;
    },
    alertClose: (state) => {
      state.isAlert = false;
    },
    recordCountSet: (state, action) => {
      state.recordCount = action?.payload;
    },
    resetGridData: (state) => {
      state.searchResult = [];
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(autocompleteSearch.pending, (state) => {
        state.loading = true;
        state.autocompleteResult = [];
        state.totalCount = 0;
      })
      .addCase(autocompleteSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.autocompleteResult = action?.payload?.warranties;
        state.totalCount = action?.payload?.totalRecords;
      })
      .addCase(autocompleteSearch.rejected, (state, action) => {
        state.loading = false;
        state.autocompleteResult = action?.payload;
        state.totalCount = 0;
      })

      .addCase(searchWarranty.pending, (state) => {
        state.loadingSearch = true;
        state.searchResult = [];
        state.isResultsFound = false;
        state.totalCount = 0;
      })
      .addCase(searchWarranty.fulfilled, (state, action) => {
        state.loadingSearch = false;
        state.searchResult = [];
        let searchDataForId = action?.payload?.warranties?.sort(
          (a, b) => a?.warrantyPartNumber - b?.warrantyPartNumber
        );
        if (action?.payload) {
          state.searchResult = searchDataForId?.map((item, i) => {
            item.newRowId = i;
            return item;
          });
        }
        state.isResultsFound = true;
        state.totalCount = action?.payload?.totalRecords || 0;
      })
      .addCase(searchWarranty.rejected, (state) => {
        state.loadingSearch = false;
        state.searchResult = [];
        state.isResultsFound = false;
        state.totalCount = 0;
      })

      .addCase(fetchDetail.pending, (state) => {
        state.loading = true;
        state.detailError = null;
        state.isWPNFound = API_RESPONSE_STATUS.pending;
      })
      .addCase(fetchDetail.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action?.payload?.data || [];

        if (responseData?.warrantyId && responseData !== '') {
          state.isWPNFound = API_RESPONSE_STATUS.success;
          state.warrantyId = responseData?.warrantyId || 0;
          state.warrantyDetail = responseData || [];
        } else {
          state.isWPNFound = API_RESPONSE_STATUS.fail;
          state.warrantyDetail = [];
          state.loading = false;
        }
      })
      .addCase(fetchDetail.rejected, (state) => {
        state.isWPNFound = API_RESPONSE_STATUS.fail;
        state.warrantyDetail = [];
        state.loading = false;
      })

      .addCase(fetchEntitlement.pending, (state) => {
        state.loading = true;
        state.isEntitlementLoading = true;
        state.entitlementError = null;
      })
      .addCase(fetchEntitlement.fulfilled, (state, action) => {
        state.loading = false;
        state.isEntitlementLoading = false;
        state.warrantyEntitlement = action?.payload || [];
      })
      .addCase(fetchEntitlement.rejected, (state) => {
        state.loading = false;
        state.isEntitlementLoading = false;
        state.warrantyEntitlement = [];
      })

      .addCase(fetchHistory.pending, (state) => {
        state.loading = true;
        state.isHistoryLoading = true;
        state.warrantySerialNoHistory = [];
      })
      .addCase(fetchHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.isHistoryLoading = false;
        state.warrantySerialNoHistory = action?.payload || [];
      })
      .addCase(fetchHistory.rejected, (state) => {
        state.loading = false;
        state.isHistoryLoading = false;
        state.warrantySerialNoHistory = [];
      });
  },
});

export const {
  handleAlertMessage,
  setIsSearchPage,
  setIsDisplayButton,
  setRadioSelectedOption,
  setAutocompleteOption,
  setCurrentSerialNumber,
  setWarrantyPartNumber,
  handleSearchTerm,
  handleSearchTermObj,
  handleReset,
  setWarrantyId,
  setDetailWarrantyNumber,
  setFilterOptions,
  wnErrorSet,
  snErrorSet,
  alertOpen,
  alertClose,
  recordCountSet,
  resetGridData,
} = warrantySlice.actions;
export default warrantySlice.reducer;
